/**
 * Blog — public long-form editorial index + detail pages.
 *
 * `/blog`         — paginated list of published posts
 * `/blog/:slug`   — full post with Article JSON-LD, GA-tracked
 *
 * Uses `PageSEO` + `buildArticleSchema` so each post ranks on its own
 * topic in Google Discover / Search. Renders markdown via the existing
 * `mdToHtml` helper so we don't ship a heavy MD library.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import PageHero from '../components/PageHero';
import PageSEO, { buildArticleSchema } from '../components/PageSEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { mdToHtml } from '../lib/richText';
import { format, parseISO } from 'date-fns';
import { ArrowLeft, BookOpen, Clock, Tag, Loader2 } from 'lucide-react';

const BLOG_SITE = 'https://freezedryacademy.com';

const PostCard = ({ post }) => {
  const date = post.published_at || post.created_at;
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="block group"
      data-testid={`blog-card-${post.slug}`}
    >
      <Card className="overflow-hidden transition hover:shadow-lg hover:-translate-y-0.5">
        {post.hero_image_url && (
          <img
            src={post.hero_image_url}
            alt=""
            className="w-full h-40 object-cover"
            loading="lazy"
          />
        )}
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">
            {post.category || 'Article'} · {post.reading_minutes || 3} min read
          </div>
          <h3 className="font-heading text-lg font-extrabold leading-tight mb-1 group-hover:text-primary">
            {post.title}
          </h3>
          {post.subtitle && (
            <p className="text-sm text-foreground/70 mb-2 line-clamp-2">{post.subtitle}</p>
          )}
          {post.excerpt && (
            <p className="text-xs text-muted-foreground line-clamp-3">{post.excerpt}</p>
          )}
          <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
            {post.author_name && <span>{post.author_name}</span>}
            {date && <>· <span>{format(parseISO(date), 'MMM d, yyyy')}</span></>}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export const BlogList = () => {
  const { API } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/blog/posts`, {
          params: { limit: 30 },
        });
        setPosts(data?.posts || []);
      } catch { /* soft-fail — page still renders */ }
      finally { setLoading(false); }
    })();
  }, [API]);

  return (
    <div className="min-h-screen bg-background" data-testid="blog-list-page">
      <PageSEO
        title="Freeze Drying Blog"
        description="Practical guides, cycle-design deep dives, and business playbooks for home and commercial freeze-drying operators."
        path="/blog"
        type="website"
      />
      <PageHero
        eyebrow="Editorial"
        eyebrowIcon={BookOpen}
        title="Freeze Drying Blog"
        description="Long-form guides, cycle-design deep dives, and business playbooks."
        testId="blog-hero"
      />
      <div className="container mx-auto max-w-5xl px-4 py-8">
        {loading ? (
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading posts…
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center">
            <div className="font-heading text-lg font-extrabold mb-1">No posts yet</div>
            <p className="text-sm text-muted-foreground">
              Long-form editorial coming soon. Bookmark this page — we publish weekly.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="blog-list-grid">
            {posts.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export const BlogPost = () => {
  const { API } = useContext(AuthContext);
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/blog/posts/${slug}`);
        if (!cancelled) setPost(data);
      } catch {
        if (!cancelled) setPost(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, slug]);

  const html = useMemo(
    () => (post?.body_markdown ? mdToHtml(post.body_markdown) : ''),
    [post?.body_markdown],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6">
        <div className="text-lg font-heading font-extrabold">Post not found</div>
        <Button variant="outline" onClick={() => navigate('/blog')} data-testid="blog-not-found-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to blog
        </Button>
      </div>
    );
  }

  const publishedAt = post.published_at || post.created_at;

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      <PageSEO
        title={post.title}
        description={post.excerpt || post.subtitle}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.hero_image_url}
        schema={buildArticleSchema({
          ...post,
          url: `${BLOG_SITE}/blog/${post.slug}`,
        })}
      />

      <div className="container mx-auto max-w-3xl px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/blog')}
          className="mb-3 rounded-full"
          data-testid="blog-post-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to blog
        </Button>

        <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-2 inline-flex items-center gap-2">
          {post.category || 'Article'}
          <span className="text-muted-foreground inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {post.reading_minutes || 3} min read
          </span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold leading-tight mb-2">
          {post.title}
        </h1>
        {post.subtitle && (
          <p className="text-lg text-foreground/75 mb-4">{post.subtitle}</p>
        )}
        <div className="flex items-center gap-2 mb-6 text-xs text-muted-foreground">
          {post.author_avatar_url ? (
            <img src={post.author_avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-primary/15 text-primary font-heading font-extrabold flex items-center justify-center text-[10px]">
              {(post.author_name || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <span className="font-semibold text-foreground/85">{post.author_name || 'FDAcademy'}</span>
          {publishedAt && <>· <span>{format(parseISO(publishedAt), 'MMMM d, yyyy')}</span></>}
        </div>
        {post.hero_image_url && (
          <img
            src={post.hero_image_url}
            alt=""
            className="w-full rounded-2xl mb-6 object-cover"
          />
        )}
        <article
          className="prose prose-slate dark:prose-invert max-w-none prose-h2:font-heading prose-h2:font-extrabold prose-h3:font-heading prose-h3:font-extrabold prose-a:text-primary"
          data-testid="blog-post-body"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {Array.isArray(post.tags) && post.tags.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-1.5" data-testid="blog-post-tags">
            {post.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 text-[11px] rounded-full border border-border bg-muted/40 px-2 py-1"
              >
                <Tag className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;
